import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSessionCheck, useUser, authKeys } from "../api/queries";
import { clearCacheOnLogout } from "../api/cache";
import { useBoundStore } from "@/stores";
import { AccountStatus, INVALID_SESSION_STATUSES } from "../types";
import { isProtectedRoute } from "../utils/routeAuth";
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { captureEvent } from "@/shared/posthog/analytics";
import { PostHogEvents } from "@/shared/posthog/events";
import { resetUser } from "@/shared/posthog/identify";

/**
 * AuthProvider — single source of truth for auth state.
 *
 * 1. Always runs a lightweight session check (GET /auth/session) to determine
 *    isAuthenticated + accountStatus on every route.
 * 2. Conditionally fetches the full user (GET /users/me) only when authenticated
 *    AND on a protected route.
 * 3. Is the ONLY writer to the Zustand auth store. No other component should
 *    call setAuth for auth state.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const setAuth = useBoundStore.use.setAuth();
    const clearAuth = useBoundStore.use.clearAuth();

    // Step 1: Lightweight session check — runs on every route
    const { data: sessionData, isLoading: isSessionLoading, isError, error } = useSessionCheck();

    const isAuthenticated = !isSessionLoading && !isError && !!sessionData?.authenticated;

    // Step 2: Full user fetch — only when ACTIVE (backend returns 401 for other statuses)
    const shouldFetchUser =
        isAuthenticated &&
        isProtectedRoute(location.pathname) &&
        sessionData?.status === AccountStatus.ACTIVE;
    const { data: user, isLoading: isUserLoading } = useUser({ enabled: shouldFetchUser });

    // Apply user's language preference only once per user session (e.g. after login), not on every
    // navigation. Otherwise we overwrite in-session language changes before the updated preference
    // is refetched from the API.
    const appliedUserLanguageRef = useRef(false);

    // Single effect that writes to the store
    useEffect(() => {
        if (isSessionLoading) return;

        // --- Session check failed (network error, etc.) ---
        if (isError) {
            appliedUserLanguageRef.current = false;
            if (globalThis.logger) {
                globalThis.logger.warn("Session check failed:", error);
            }
            setAuth({
                user: null,
                isAuthenticated: false,
                isAuthLoading: false,
                accountStatus: null,
            });
            return;
        }

        // --- Not authenticated ---
        if (!sessionData || !sessionData.authenticated) {
            appliedUserLanguageRef.current = false;
            setAuth({
                user: null,
                isAuthenticated: false,
                isAuthLoading: false,
                accountStatus: null,
            });
            return;
        }

        // --- Blocked / Deleted ---
        if (INVALID_SESSION_STATUSES.includes(sessionData.status)) {
            appliedUserLanguageRef.current = false;
            clearCacheOnLogout(queryClient, {
                sessionCheckKey: authKeys.sessionCheck(),
                userKey: authKeys.user(),
            });
            clearAuth();
            captureEvent(PostHogEvents.LOGOUT);
            resetUser();
            const message =
                sessionData.status === "BLOCKED"
                    ? i18n.t("auth:account.blocked")
                    : i18n.t("auth:account.deleted");
            toast.error(message);
            navigate("/", { replace: true });
            return;
        }

        // --- Authenticated: merge session check + user data ---
        // Session check is the authority on isAuthenticated and accountStatus.
        // User query only enriches with the full user object when available.
        if (shouldFetchUser && isUserLoading) {
            setAuth({
                isAuthenticated: true,
                accountStatus: sessionData.status,
                isAuthLoading: true,
            });
            return;
        }

        if (shouldFetchUser && user) {
            setAuth({
                user,
                isAuthenticated: true,
                isAuthLoading: false,
                accountStatus: user.status ?? sessionData.status,
            });
            if (user.languagePreference && !appliedUserLanguageRef.current) {
                appliedUserLanguageRef.current = true;
                useBoundStore.getState().setLanguage(user.languagePreference);
                i18n.changeLanguage(user.languagePreference);
            }
        } else {
            // Guest route, or non-ACTIVE status (we don't fetch /me) — session check is the authority
            setAuth({
                isAuthenticated: true,
                accountStatus: sessionData.status,
                isAuthLoading: false,
            });
        }
    }, [
        isSessionLoading, isError, error, sessionData,
        shouldFetchUser, isUserLoading, user,
        setAuth, clearAuth, navigate, queryClient, location.pathname,
    ]);

    // No loader here: bootstrap loader is shown by AppConfigProvider (config + session) to avoid blink.
    return <>{children}</>;
};
